/** Centre of the chip area in viewBox units; the logomark and the signal-arrival glow both sit here. */
const CHIP_CENTER = { cx: 236, cy: 56 };

// Each electron travels inward over this fraction of its cycle, then rests (hidden) for the remainder,
// so signals arrive intermittently rather than as a constant stream. Desynced durs keep them apart.
const TRAVEL_FRACTION = 0.6;

/**
 * One inbound signal: a decorative dot that travels a trace toward the chip, plus the pins that flash
 * when it arrives. Not a domain entity — purely the hero's "signal flows in and lights up the chip"
 * motion. Timing per flow is chosen so the two signals rarely line up and the motion reads as live
 * rather than metronomic.
 */
interface SignalFlow {
	/** Path geometry, identical to one of the rendered traces so the dot tracks it exactly. */
	path: string;
	/** Seconds for one traversal. */
	dur: number;
	/** Seconds to delay the first traversal. */
	begin: number;
	/** Chip-edge pins that flash the instant this signal lands. */
	pins: { cx: number; cy: number }[];
}

const SIGNAL_FLOWS: SignalFlow[] = [
	{
		path: 'M-6 24 H88 L108 44 H200',
		dur: 5,
		begin: 0,
		pins: [
			{ cx: 146, cy: 56 },
			{ cx: 166, cy: 56 },
		],
	},
	{
		path: 'M478 90 H398 L376 70 H272',
		dur: 6.4,
		begin: 2.2,
		pins: [
			{ cx: 326, cy: 56 },
			{ cx: 306, cy: 56 },
		],
	},
];

const TRACE_DIM = 'var(--color-blue-a7, oklch(60.9% 0.121 230 / 0.45))';
const TRACE_BRIGHT = 'var(--color-blue-9, oklch(60.9% 0.121 230))';

/** Dim background traces, rendered first so the bright signal lines and nodes sit on top. */
const BASE_TRACES = [
	'M-6 24 H88 L108 44 H200',
	'M-6 90 H74 L96 70 H200',
	'M-6 56 H200',
	'M146 56 V36 H184',
	'M166 56 V78 H192',
	'M478 24 H384 L364 44 H272',
	'M478 90 H398 L376 70 H272',
	'M478 56 H272',
	'M326 56 V36 H288',
	'M306 56 V78 H280',
];

/** Brighter "live" traces overlaid on the dim ones to suggest active signal paths. */
const SIGNAL_TRACES = ['M-6 24 H88 L108 44 H200', 'M478 90 H398 L376 70 H272', 'M-6 56 H200', 'M478 56 H272'];

/** Junction nodes (canvas-filled, ringed) where traces bend into the central chip area. */
const JUNCTION_NODES = [
	{ cx: 108, cy: 44 },
	{ cx: 96, cy: 70 },
	{ cx: 364, cy: 44 },
	{ cx: 376, cy: 70 },
];

/** Solid pin dots on the central chip edges. */
const PIN_DOTS = [
	{ cx: 146, cy: 56 },
	{ cx: 166, cy: 56 },
	{ cx: 326, cy: 56 },
	{ cx: 306, cy: 56 },
];

/** Pad rectangles flanking the central chip. */
const PADS = [
	{ x: 180, y: 33.5 },
	{ x: 284, y: 33.5 },
	{ x: 188, y: 75.5 },
	{ x: 276, y: 75.5 },
];

// The arrival keyframes — flash in just before the electron lands (TRAVEL_FRACTION), then decay over the
// rest of the cycle — shared by the chip glow and the pin flashes so they fire on the same beat.
const ARRIVAL_KEY_TIMES = `0;${TRAVEL_FRACTION - 0.06};${TRAVEL_FRACTION};1`;
const GLOW_OPACITY_VALUES = '0;0;0.15;0';
const PIN_FLASH_OPACITY_VALUES = '0;0;0.95;0';
const PIN_FLASH_RADIUS_VALUES = '1.8;1.8;6;7';

/**
 * Circuit-trace illustration with the Cadstrom logomark centred in the gap the artwork leaves open
 * for it. The SVG is inlined so the animated "electrons" share its coordinate space and stay exactly
 * on the traces. The hero is reactive: a signal drifts inward along a trace and, the moment it reaches
 * the chip, a cyan glow blooms behind the logomark and the chip's pins flash — a quiet visualization
 * of a design arriving and being read. All motion is hidden for users who prefer reduced motion; the
 * traces, nodes, and logomark stay fully visible as the resting state.
 */
export function EmptyStateHero() {
	return (
		<div className='relative flex w-full items-center justify-center'>
			<svg viewBox='0 0 472 112' fill='none' aria-hidden className='w-[108%] max-w-none select-none'>
				<defs>
					<radialGradient id='chip-arrival-glow'>
						<stop offset='0%' stopColor={TRACE_BRIGHT} stopOpacity='0.85' />
						<stop offset='55%' stopColor={TRACE_BRIGHT} stopOpacity='0.22' />
						<stop offset='100%' stopColor={TRACE_BRIGHT} stopOpacity='0' />
					</radialGradient>
				</defs>

				{/* Faint always-on halo so the chip reads as powered even between signals. */}
				<ellipse
					cx={CHIP_CENTER.cx}
					cy={CHIP_CENTER.cy}
					rx={38}
					ry={24}
					fill='url(#chip-arrival-glow)'
					opacity={0.03}
				/>

				<g className='motion-reduce:hidden'>
					{SIGNAL_FLOWS.map((flow) => (
						<ellipse
							key={`glow-${flow.path}`}
							cx={CHIP_CENTER.cx}
							cy={CHIP_CENTER.cy}
							rx={42}
							ry={27}
							fill='url(#chip-arrival-glow)'
							opacity={0}
						>
							<animate
								attributeName='opacity'
								dur={`${flow.dur}s`}
								begin={`${flow.begin}s`}
								repeatCount='indefinite'
								values={GLOW_OPACITY_VALUES}
								keyTimes={ARRIVAL_KEY_TIMES}
							/>
						</ellipse>
					))}
				</g>

				<g stroke={TRACE_DIM} strokeWidth={1.1} opacity={0.6} strokeLinecap='round' strokeLinejoin='round'>
					{BASE_TRACES.map((d) => (
						<path key={d} d={d} />
					))}
				</g>

				<g stroke={TRACE_BRIGHT} strokeWidth={1.4} strokeLinecap='round' strokeLinejoin='round'>
					{SIGNAL_TRACES.map((d) => (
						<path key={d} d={d} />
					))}
				</g>

				<g fill={TRACE_DIM}>
					{PADS.map((pad) => (
						<rect key={`${pad.x}-${pad.y}`} x={pad.x} y={pad.y} width={8} height={5} rx={1} />
					))}
				</g>

				<g fill='var(--color-background, #09151b)' stroke={TRACE_BRIGHT} strokeWidth={1.1}>
					{JUNCTION_NODES.map((node) => (
						<circle key={`${node.cx}-${node.cy}`} cx={node.cx} cy={node.cy} r={3.2} />
					))}
				</g>

				<g fill={TRACE_BRIGHT}>
					{PIN_DOTS.map((dot) => (
						<circle key={`${dot.cx}-${dot.cy}`} cx={dot.cx} cy={dot.cy} r={1.8} />
					))}
				</g>

				{/* Pin flashes: an expanding ring that fires on the same beat as its signal's arrival. */}
				<g className='motion-reduce:hidden' fill='none' stroke='var(--color-blue-11)' strokeWidth={1.2}>
					{SIGNAL_FLOWS.flatMap((flow) =>
						flow.pins.map((pin) => (
							<circle key={`flash-${pin.cx}-${pin.cy}`} cx={pin.cx} cy={pin.cy} r={1.8} opacity={0}>
								<animate
									attributeName='opacity'
									dur={`${flow.dur}s`}
									begin={`${flow.begin}s`}
									repeatCount='indefinite'
									values={PIN_FLASH_OPACITY_VALUES}
									keyTimes={ARRIVAL_KEY_TIMES}
								/>
								<animate
									attributeName='r'
									dur={`${flow.dur}s`}
									begin={`${flow.begin}s`}
									repeatCount='indefinite'
									values={PIN_FLASH_RADIUS_VALUES}
									keyTimes={ARRIVAL_KEY_TIMES}
								/>
							</circle>
						)),
					)}
				</g>

				<g className='motion-reduce:hidden'>
					{SIGNAL_FLOWS.map((flow) => (
						<circle key={`electron-${flow.path}`} r={2.4} fill='var(--color-blue-11)'>
							<animateMotion
								dur={`${flow.dur}s`}
								begin={`${flow.begin}s`}
								repeatCount='indefinite'
								calcMode='linear'
								path={flow.path}
								keyPoints='0;1;1'
								keyTimes={`0;${TRAVEL_FRACTION};1`}
							/>
							<animate
								attributeName='opacity'
								dur={`${flow.dur}s`}
								begin={`${flow.begin}s`}
								repeatCount='indefinite'
								values='0;1;1;0;0'
								keyTimes={`0;0.03;${TRAVEL_FRACTION - 0.04};${TRAVEL_FRACTION};1`}
							/>
						</circle>
					))}
				</g>
			</svg>

			<img
				src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png'
				alt=''
				className='absolute h-16 w-16 object-contain select-none'
				draggable={false}
			/>
		</div>
	);
}
